import { routePath } from '@/app-shell/layout/Navigation/sidebar/nav-item.utils';
import { cn } from '@/lib/cn';
import { useRouter } from 'next/router';
import { useCallback, type ReactNode } from 'react';
import { usePendingNavHref } from './hooks/useIsRouteActive';
import { NavigationLoadingView } from './navigation-loading-view';

/** Keep the current page (including recording/discard guards) alive until Next
 * accepts the navigation, while immediately replacing its visible content. */
export const NavigationContentBoundary = ({ children }: { children: ReactNode }) => {
  const pendingHref = usePendingNavHref();
  const { asPath } = useRouter();
  const isPending = pendingHref !== null && pendingHref !== routePath(asPath);

  // React 18 does not forward boolean inert props. Set it during commit,
  // before the next keyboard event; retain the ref across unrelated renders.
  const setContentRef = useCallback(
    (element: HTMLDivElement | null) => {
      element?.toggleAttribute('inert', isPending);
    },
    [isPending]
  );

  return (
    <>
      <div
        className={cn('contents', isPending && 'invisible')}
        ref={setContentRef}
        aria-hidden={isPending || undefined}
      >
        {children}
      </div>
      {isPending && (
        <div className="absolute inset-0 z-50 flex flex-col">
          <NavigationLoadingView />
        </div>
      )}
    </>
  );
};
